/**
 * The URL is the state of this screen.
 *
 * Filters, search, sort, page and page size all live in the query string and nowhere
 * else: no `useState` mirrors any of them. That is what makes a filtered view
 * shareable as a link, which is the phase's exit criterion, and it also means the
 * back button works without anything being written to make it work.
 *
 * Everything read out of a URL is validated. A link can be a month old, hand-edited,
 * or from a build where an enum had a different value, and the API answers a bad
 * `status` or `sort_by` with a 422 rather than ignoring it. A stale bookmark has to
 * degrade to an unfiltered list, never to an error screen.
 */

import type { CandidateFilters } from './api';
import {
  DEFAULT_SORT_BY,
  DEFAULT_SORT_DIR,
  SORTABLE_KEYS,
  type CandidateSortKey,
} from './columns';
import {
  APPLYING_FOR_LABEL,
  DOCUMENTATION_LABEL,
  FLYST_LABEL,
  PAYMENT_LABEL,
  RSR_LABEL,
  SOURCE_LABEL,
  STATUS_LABEL,
} from './labels';

/** 100 is the API's hard cap; it answers anything larger with a 422. */
export const PER_PAGE_OPTIONS = [25, 50, 100] as const;
export const DEFAULT_PER_PAGE = 25;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** What the API accepts on `search` before it answers with a 422. */
export const MAX_SEARCH_LENGTH = 200;

const MIN_POINTS = 0;
const MAX_POINTS = 5;

export const EMPTY_FILTERS: CandidateFilters = {
  page: 1,
  per_page: DEFAULT_PER_PAGE,
  search: '',
  status: '',
  source: '',
  applying_for: '',
  department_id: '',
  preferred_position_id: '',
  preferred_country_id: '',
  flyst_stage: '',
  rsr_state: '',
  documentation: '',
  payment_status: '',
  contacted: '',
  received_from: '',
  received_to: '',
  points_min: '',
  points_max: '',
  sort_by: DEFAULT_SORT_BY,
  sort_dir: DEFAULT_SORT_DIR,
};

/** The filters that actually narrow the list (page, sort and size are not filters). */
const FILTER_KEYS = [
  'search',
  'status',
  'source',
  'applying_for',
  'department_id',
  'preferred_position_id',
  'preferred_country_id',
  'flyst_stage',
  'rsr_state',
  'documentation',
  'payment_status',
  'contacted',
  'received_from',
  'received_to',
  'points_min',
  'points_max',
] as const satisfies readonly (keyof CandidateFilters)[];

/** The four with a control always on screen. The rest sit behind the disclosure. */
const PRIMARY_FILTER_KEYS = ['search', 'status', 'department_id', 'source'] as const;

/** Keep the value only if it names something the API knows about. */
function oneOf<T extends string>(raw: string | null, labels: Record<T, string>): T | '' {
  return raw !== null && Object.hasOwn(labels, raw) ? (raw as T) : '';
}

function uuid(raw: string | null): string {
  return raw !== null && UUID.test(raw) ? raw : '';
}

function isoDate(raw: string | null): string {
  if (raw === null || !ISO_DATE.test(raw)) return '';

  const parsed = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return '';

  // The shape being right is not enough. JavaScript rolls an impossible day forward
  // rather than rejecting it (2026-02-31 becomes the 3rd of March), so the only way
  // to know the date exists is to write it back out and compare. The API is stricter
  // and answers 2026-02-31 with a 422, which is the error screen this exists to avoid.
  // Every part padded, the year included. getFullYear() returns 2 for the year 2,
  // so an unpadded comparison rejects every year below 1000, and a browser's date
  // input emits exactly those while somebody is typing the year one digit at a time.
  const year = String(parsed.getFullYear()).padStart(4, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}` === raw ? raw : '';
}

function points(raw: string | null): string {
  if (raw === null || !/^(0|[1-4](?:\.5)?|5)$/.test(raw)) return '';
  const value = Number(raw);
  return value >= MIN_POINTS && value <= MAX_POINTS ? String(value) : '';
}

export function parseFilters(params: URLSearchParams): CandidateFilters {
  const rawPage = Number(params.get('page'));
  const rawPerPage = Number(params.get('per_page'));
  const sortBy = params.get('sort_by');
  const sortDir = params.get('sort_dir');
  const contacted = params.get('contacted');

  return {
    page: Number.isInteger(rawPage) && rawPage >= 1 ? rawPage : 1,
    per_page: (PER_PAGE_OPTIONS as readonly number[]).includes(rawPerPage)
      ? rawPerPage
      : DEFAULT_PER_PAGE,

    // Truncated, not dropped: the server caps the term at 200 characters and answers
    // anything longer with a 422, which on this screen would be an error page in
    // place of the register.
    search: params.get('search')?.trim().slice(0, MAX_SEARCH_LENGTH) ?? '',

    status: oneOf(params.get('status'), STATUS_LABEL),
    source: oneOf(params.get('source'), SOURCE_LABEL),
    applying_for: oneOf(params.get('applying_for'), APPLYING_FOR_LABEL),
    flyst_stage: oneOf(params.get('flyst_stage'), FLYST_LABEL),
    rsr_state: oneOf(params.get('rsr_state'), RSR_LABEL),
    documentation: oneOf(params.get('documentation'), DOCUMENTATION_LABEL),
    payment_status: oneOf(params.get('payment_status'), PAYMENT_LABEL),

    // A well-formed but unknown id is kept: the API answers with an empty page, which
    // is a truthful answer to "show me candidates in a sector that no longer exists".
    department_id: uuid(params.get('department_id')),
    preferred_position_id: uuid(params.get('preferred_position_id')),
    preferred_country_id: uuid(params.get('preferred_country_id')),

    contacted: contacted === 'true' || contacted === 'false' ? contacted : '',

    received_from: isoDate(params.get('received_from')),
    received_to: isoDate(params.get('received_to')),
    points_min: points(params.get('points_min')),
    points_max: points(params.get('points_max')),

    sort_by: (SORTABLE_KEYS as readonly string[]).includes(sortBy ?? '')
      ? (sortBy as CandidateSortKey)
      : DEFAULT_SORT_BY,
    sort_dir: sortDir === 'asc' || sortDir === 'desc' ? sortDir : DEFAULT_SORT_DIR,
  };
}

/**
 * Filters back into a URL, leaving out anything sitting at its default.
 *
 * An unfiltered list is `/candidates` with no query string at all, and a shared link
 * carries only what the sender actually changed, which is what makes it readable
 * when it arrives in a message.
 */
export function toSearchParams(filters: CandidateFilters): URLSearchParams {
  const params = new URLSearchParams();

  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (value !== '') params.set(key, value);
  }

  if (filters.sort_by !== DEFAULT_SORT_BY) params.set('sort_by', filters.sort_by);
  if (filters.sort_dir !== DEFAULT_SORT_DIR) params.set('sort_dir', filters.sort_dir);
  if (filters.per_page !== DEFAULT_PER_PAGE) params.set('per_page', String(filters.per_page));
  if (filters.page !== 1) params.set('page', String(filters.page));

  return params;
}

/**
 * Is anything actually narrowing the list?
 *
 * Page, sort and page size deliberately do not count. Paging to page 3 must not light
 * up a "clear filters" button, and it must not turn an empty last page into the
 * "nothing matches your filters" message.
 */
export function isFiltered(filters: CandidateFilters): boolean {
  return FILTER_KEYS.some((key) => filters[key] !== '');
}

/** Something is set that has no control on screen until the disclosure is open. */
export function hasAdvancedFilter(filters: CandidateFilters): boolean {
  const primary = new Set<string>(PRIMARY_FILTER_KEYS);
  return FILTER_KEYS.some((key) => !primary.has(key) && filters[key] !== '');
}
